import { useAction, useMutation, useQuery } from "convex/react"
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from "react"
import { useInterval } from "usehooks-ts"
import { useAppAuth } from "@/hooks/useAppAuth"
import { useIdleReady } from "@/hooks/useIdleReady"
import { api } from "~convex/_generated/api"

const HEARTBEAT_INTERVAL_MS = 30_000

function useWebBalanceInternal() {
	const { isAuthenticated, currentUser } = useAppAuth()
	const idleReady = useIdleReady(isAuthenticated)
	const walletAddress = currentUser?.wallet_address ?? null

	const heartbeat = useMutation(api.balanceCache.heartbeat)
	const refreshAction = useAction(api.balanceCache.refreshMyWallet)
	const balances = useQuery(
		api.balanceCache.getBalances,
		walletAddress ? { walletAddress } : "skip",
	)

	const usdcBalance = balances?.usdc ?? 0
	const captchaBalance = balances?.captcha ?? 0
	const usdcLoading = isAuthenticated && (balances === undefined || balances.usdc === null)
	const captchaLoading = isAuthenticated && (balances === undefined || balances.captcha === null)

	const heartbeatRef = useRef(heartbeat)
	heartbeatRef.current = heartbeat
	const inFlightRef = useRef(false)

	const sendHeartbeat = useCallback(async () => {
		if (inFlightRef.current) {
			return
		}
		inFlightRef.current = true
		try {
			await heartbeatRef.current({})
		} catch {
			// silent — reactive query handles transient disconnects
		} finally {
			inFlightRef.current = false
		}
	}, [])

	useEffect(() => {
		if (isAuthenticated && idleReady && walletAddress) {
			sendHeartbeat().catch(() => {})
		}
	}, [idleReady, isAuthenticated, walletAddress, sendHeartbeat])

	useInterval(
		() => {
			sendHeartbeat().catch(() => {})
		},
		isAuthenticated && idleReady && walletAddress ? HEARTBEAT_INTERVAL_MS : null,
	)

	const refetch = useCallback(() => {
		refreshAction({}).catch(() => {})
	}, [refreshAction])

	return {
		usdcBalance,
		captchaBalance,
		usdcLoading,
		captchaLoading,
		refetchUSDC: refetch,
		refetchAll: refetch,
	}
}

type WebBalanceState = ReturnType<typeof useWebBalanceInternal>

const WebBalanceContext = createContext<WebBalanceState>({
	captchaBalance: 0,
	captchaLoading: true,
	refetchAll: () => {},
	refetchUSDC: () => {},
	usdcBalance: 0,
	usdcLoading: true,
})

export function WebBalanceProvider({ children }: { children: ReactNode }) {
	const { usdcBalance, captchaBalance, usdcLoading, captchaLoading, refetchUSDC, refetchAll } =
		useWebBalanceInternal()
	const memo = useMemo(
		() => ({
			usdcBalance,
			captchaBalance,
			usdcLoading,
			captchaLoading,
			refetchUSDC,
			refetchAll,
		}),
		[usdcBalance, captchaBalance, usdcLoading, captchaLoading, refetchUSDC, refetchAll],
	)
	return <WebBalanceContext.Provider value={memo}>{children}</WebBalanceContext.Provider>
}

export function useWebBalance() {
	return useContext(WebBalanceContext)
}
